<script setup lang="ts">
	import { computed, ref, toRefs } from 'vue'

	import { useAuth } from '@/composables/useAuth'
	import useChannel from '@/composables/useChannel'
	import useServices from '@/composables/useServices'

	import { Company } from '@/modules/companies/interfaces/company'

	interface Props {
		activeFilters: Record<string, any>[]
		params: Record<string, any>
		selectedCompany: Company | undefined
	}

	interface Emit {
		(e: 'resetFilters'): void
		(e: 'searchParams'): void
		(e: 'handleSelectedCompany', value: Company | undefined): void
	}

	const props = defineProps<Props>()

	const emits = defineEmits<Emit>()

	const { activeFilters, params } = toRefs(props)
	const { user } = useAuth()

	const { services } = useServices()
	const { channels } = useChannel()

	// Estado para controlar la vista expandida/colapsada
	const isExpanded = ref(false)
	const showAllFilters = ref(false)

	const filterDisplayNames: Record<string, string> = {
		travelerName: 'Viajero',
		confirmation: 'Confirmación',
		name: 'Nombre itinerario',
		company: 'Empresa',
		status: 'Estatus',
		progressStage: 'Etapa',
		order: 'Orden',
		creatorName: 'Creador',
		service: 'Servicio',
		channel: 'Canal',
		folio: 'Folio',
		hasLetter: 'Carta garantía',
		dates: 'Fechas de viaje',
		creationDates: 'Fechas de creación',
		supplierName: 'Proveedor',
		tripReason: 'Motivo de viaje',
		cef: 'CEF',
	}

	const getFilterIcon = (reference: string) => {
		const iconMap: Record<string, string> = {
			travelerName: 'person',
			confirmation: 'confirmation_number',
			name: 'travel_explore',
			company: 'business',
			status: 'task_alt',
			progressStage: 'timeline',
			order: 'sort',
			creatorName: 'person_add',
			service: 'room_service',
			channel: 'campaign',
			folio: 'receipt',
			hasLetter: 'mail',
			dates: 'date_range',
			creationDates: 'schedule',
			supplierName: 'store',
			tripReason: 'luggage',
			cef: 'confirmation_number',
		}
		return iconMap[reference] || 'label'
	}

	const getFilterColor = (reference: string) => {
		const colorMap: Record<string, string> = {
			travelerName: 'border-blue-200 bg-blue-50 text-blue-700',
			confirmation: 'border-purple-200 bg-purple-50 text-purple-700',
			name: 'border-green-200 bg-green-50 text-green-700',
			company: 'border-orange-200 bg-orange-50 text-orange-700',
			status: 'border-red-200 bg-red-50 text-red-700',
			progressStage: 'border-indigo-200 bg-indigo-50 text-indigo-700',
			order: 'border-gray-200 bg-gray-50 text-gray-700',
			creatorName: 'border-teal-200 bg-teal-50 text-teal-700',
			service: 'border-cyan-200 bg-cyan-50 text-cyan-700',
			channel: 'border-pink-200 bg-pink-50 text-pink-700',
			folio: 'border-yellow-200 bg-yellow-50 text-yellow-700',
			hasLetter: 'border-emerald-200 bg-emerald-50 text-emerald-700',
			dates: 'border-violet-200 bg-violet-50 text-violet-700',
			creationDates: 'border-rose-200 bg-rose-50 text-rose-700',
			supplierName: 'border-lime-200 bg-lime-50 text-lime-700',
			tripReason: 'border-sky-200 bg-sky-50 text-sky-700',
			cef: 'border-amber-200 bg-amber-50 text-amber-700',
		}
		return colorMap[reference] || 'border-gray-200 bg-gray-50 text-gray-700'
	}

	const getFormattedValue = (key: string, value: any) => {
		if (value === null || value === undefined || value === '') return null

		if (key === 'dates' || key === 'creationDates') {
			if (Array.isArray(value) && value.length > 0) {
				return value
					.map((date) => new Date(date).toLocaleDateString())
					.join(' - ')
			}
			return null
		}

		if (key === 'hasLetter') {
			if (value === 1 || value === true) return 'Con carta'
			if (value === 0 || value === false) return 'Sin carta'
			if (value === null) return 'Ambos'
			return null
		}

		if (key === 'order') {
			if (value === 'desc') return 'Más reciente'
			if (value === 'asc') return 'Más antiguo'
			if (value === null) return 'Sin orden'
			return null
		}

		if (key === 'progressStage') {
			if (value === 'current') return 'En curso'
			if (value === 'past') return 'Anteriores'
			if (value === 'future') return 'Próximos'
			if (value === 'all') return 'Todos'
			return value
		}

		if (key === 'status') {
			if (value === 1) return 'Creado'
			if (value === 2) return 'Confirmado'
			if (value === 0) return 'Todos los estados'
			return value
		}

		if (key === 'service') {
			return (
				services.value.find((s) => s.id === value)?.displayName || 'Desconocido'
			)
		}

		if (key === 'channel') {
			return channels.value.find((c) => c.id === value)?.name || 'Desconocido'
		}

		if (key === 'company') {
			return props.selectedCompany?.name
		}

		return String(value)
	}

	const filtersFromParams = computed(() => {
		return Object.entries(params.value)
			.map(([key, value]) => {
				const formattedValue = getFormattedValue(key, value)
				if (!formattedValue) return null

				return {
					reference: key,
					key: filterDisplayNames[key] || key,
					value: formattedValue,
				}
			})
			.filter((filter) => filter !== null)
			.filter((filter) => {
				if (filter!.reference === 'order' && filter!.value === 'Más reciente')
					return false
				if (filter!.reference === 'progressStage') return false
				if (filter!.reference === 'status' && filter!.value === 3) {
					return false
				}
				if (filter.reference === 'page') return false
				if (filter.reference === 'perPage') return false
				return true
			}) as Array<{ reference: string; key: string; value: string }>
	})

	const removeFilter = (reference: string) => {
		if (reference === 'dates' || reference === 'creationDates') {
			params.value[reference] = []
		} else if (reference === 'order') {
			params.value[reference] = 'desc'
		} else if (reference === 'progressStage') {
			params.value[reference] = 'current'
		} else if (reference === 'company') {
			params.value[reference] = undefined
			emits('handleSelectedCompany', undefined)
		} else {
			params.value[reference] = null
		}
		emits('searchParams')
	}

	const activeFiltersCount = computed(() => {
		return filtersFromParams.value.length
	})

	const FILTERS_LIMIT = 4

	const visibleFilters = computed(() => {
		if (
			showAllFilters.value ||
			filtersFromParams.value.length <= FILTERS_LIMIT
		) {
			return filtersFromParams.value
		}
		return filtersFromParams.value.slice(0, FILTERS_LIMIT)
	})

	const hasHiddenFilters = computed(() => {
		return filtersFromParams.value.length > FILTERS_LIMIT
	})

	const hiddenFiltersCount = computed(() => {
		return Math.max(0, filtersFromParams.value.length - FILTERS_LIMIT)
	})

	const isCompactView = computed(() => {
		return filtersFromParams.value.length > 3 && !isExpanded.value
	})

	const toggleExpanded = () => {
		isExpanded.value = !isExpanded.value
	}

	const toggleShowAll = () => {
		showAllFilters.value = !showAllFilters.value
	}
</script>

<template>
	<div v-if="filtersFromParams.length > 0 || activeFilters.length > 0">
		<!-- Header mejorado con controles de vista -->
		<div class="px-4 py-3">
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<div class="flex items-center gap-2">
						<span class="material-symbols-outlined text-blue-600 text-base"
							>filter_alt</span
						>
						<h3 class="text-sm font-semibold text-gray-800">
							Filtros aplicados
						</h3>
						<span
							class="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium text-white bg-blue-500 rounded-full"
						>
							{{ activeFiltersCount }}
						</span>
					</div>

					<!-- Botón para expandir/colapsar cuando hay muchos filtros -->
					<button
						v-if="isCompactView || isExpanded"
						@click.prevent="toggleExpanded"
						class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
					>
						<span class="material-symbols-outlined text-sm">
							{{ isExpanded ? 'expand_less' : 'expand_more' }}
						</span>
						{{ isExpanded ? 'Compacto' : 'Expandir' }}
					</button>
				</div>
			</div>
		</div>

		<!-- Vista Compacta - Solo resumen -->
		<div
			v-if="isCompactView"
			class="px-4 py-3 border-b"
		>
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-2">
					<span class="text-sm text-gray-600">
						{{ activeFiltersCount }} filtros aplicados
					</span>
					<div class="flex -space-x-1">
						<div
							v-for="filter in visibleFilters.slice(0, 3)"
							:key="filter.reference"
							:class="[
								'inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-white text-xs font-bold',
								getFilterColor(filter.reference),
							]"
							:title="filter.key + ': ' + filter.value"
						>
							<span class="material-symbols-outlined text-xs opacity-80">
								{{ getFilterIcon(filter.reference) }}
							</span>
						</div>
						<div
							v-if="activeFiltersCount > 3"
							class="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-gray-100 text-gray-600 text-xs font-bold"
							:title="`+${activeFiltersCount - 3} filtros más`"
						>
							+{{ activeFiltersCount - 3 }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Vista Expandida - Lista completa de filtros -->
		<div v-else>
			<!-- Mostrar filtros limitados o todos -->
			<div class="space-y-3">
				<div class="flex flex-wrap gap-2">
					<div
						v-for="filter in visibleFilters"
						:key="filter.reference"
						:class="[
							'inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg text-xs font-medium border shadow-sm hover:shadow-md transition-all duration-200',
							getFilterColor(filter.reference),
						]"
					>
						<!-- Icono del filtro -->
						<span class="material-symbols-outlined text-sm opacity-80">
							{{ getFilterIcon(filter.reference) }}
						</span>

						<!-- Contenido del filtro -->
						<div class="flex items-center gap-1">
							<span class="font-semibold">{{ filter.key }}:</span>
							<span
								class="font-normal max-w-28 truncate"
								:title="filter.value"
							>
								{{ filter.value }}
							</span>
						</div>

						<!-- Botón para eliminar filtro individual -->
						<button
							v-if="filter.reference !== 'company' || user?.internal"
							:class="{
								hidden:
									(filter.reference === 'status' && filter.value === '3') ||
									filter.reference === 'progressStage',
							}"
							@click.prevent="removeFilter(filter.reference)"
							class="ml-0.5 p-0.5 rounded-full hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
							:title="`Eliminar filtro: ${filter.key}`"
						>
							<span class="material-symbols-outlined text-sm text-gray-500">
								close
							</span>
						</button>

						<!-- Indicador de filtro fijo para company (solo para externos) -->
						<span
							v-else
							class="material-symbols-outlined text-sm text-gray-400"
							title="Filtro fijo"
						>
							lock
						</span>
					</div>
				</div>

				<!-- Botón "Ver más" / "Ver menos" -->
				<div
					v-if="hasHiddenFilters"
					class="flex justify-center pt-2"
				>
					<button
						@click.prevent="toggleShowAll"
						class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
					>
						<span class="material-symbols-outlined text-sm">
							{{ showAllFilters ? 'expand_less' : 'expand_more' }}
						</span>
						{{ showAllFilters ? 'Ver menos' : `Ver ${hiddenFiltersCount} más` }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>
